import { Component, ChangeDetectionStrategy, signal, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { InvoiceService, InvoiceDTO } from '../../services/invoice.service';
import { SaleService, type SaleDTO } from '../../services/sale.service';
import { CustomerService, type CustomerDTO } from '../../services/customer.service';
import { SubscriptionService } from '../../services/subscription.service';
import { AuthService } from '../../services/auth.service';
import { SearchInvoiceFilterSpec } from '@/src/services/service-common';
import {
  EntityState,
  CfdiInvoiceStatusEnum,
  type ChargeDTO,
  type CfdiInvoiceStatusEnumValue,
  type SubscriptionDTO
} from '../../core/models';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { DatePickerModule } from 'primeng/datepicker';
import { MenuModule } from 'primeng/menu';
import { DialogModule } from 'primeng/dialog';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';
import { apiFailureMessage, httpErrorApiMessage } from '../../core/api-response-utils';
import { SaleReadComponent } from '../sales/sale-read.component';
import { CustomerReadComponent } from '../customers/customer-read.component';
import { SubscriptionReadComponent } from '../subscriptions/subscription-read.component';

@Component({
  selector: 'app-invoices-list',
  templateUrl: './invoices-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .invoices-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .invoices-primeng-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .invoices-primeng-table .p-datatable-table {
        width: 100%;
        table-layout: auto;
      }
      :host ::ng-deep p-table {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .invoices-primeng-table.p-datatable,
      :host ::ng-deep .p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container,
      :host ::ng-deep .p-datatable-flex-scrollable > .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .p-datatable-paginator-bottom {
        flex-shrink: 0;
      }
      :host ::ng-deep .p-paginator .p-paginator-page.p-paginator-page-selected,
      :host ::ng-deep button.p-paginator-page-selected {
        background: rgb(224 242 254) !important;
        border-color: rgb(14 165 233) !important;
        color: rgb(2 132 199) !important;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .sale-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .customer-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .subscription-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    PERMISSION_UI_DIRECTIVES,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    ButtonModule,
    SkeletonModule,
    FloatLabelModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    SelectModule,
    TagModule,
    TooltipModule,
    DrawerModule,
    DatePickerModule,
    MenuModule,
    DialogModule,
    SaleReadComponent,
    CustomerReadComponent,
    SubscriptionReadComponent
  ]
})
export class InvoicesListComponent {
  private invoiceService = inject(InvoiceService);
  private saleService = inject(SaleService);
  private customerService = inject(CustomerService);
  private subscriptionService = inject(SubscriptionService);
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private sanitizer = inject(DomSanitizer);

  @ViewChild('rowActionsMenu') rowActionsMenu?: Menu;

  readonly canExportInvoices = this.authService.hasRole('INVOICE_EXPORT');
  exporting = signal(false);

  get exportButtonTooltip(): string {
    if (!this.canExportInvoices) {
      return 'No tienes permiso para descargar este listado. Pide acceso a un administrador.';
    }
    if (this.totalItems() === 0) {
      return 'No hay facturas para exportar con los filtros actuales';
    }
    return 'Descargar todas las facturas filtradas en CSV';
  }

  selectedInvoice = signal<InvoiceDTO | null>(null);
  isDetailsOpen = signal(false);
  detailsLoading = signal(false);

  /** Drawer de detalle de venta */
  selectedSale = signal<SaleDTO | null>(null);
  saleCharges = signal<ChargeDTO[]>([]);
  isSaleDetailsOpen = signal(false);
  saleDetailsLoading = signal(false);

  /** Drawer de detalle de cliente */
  selectedCustomer = signal<CustomerDTO | null>(null);
  isCustomerDetailsOpen = signal(false);
  customerDetailsLoading = signal(false);

  /** Drawer de detalle de suscripción (desde venta) */
  selectedSubscription = signal<SubscriptionDTO | null>(null);
  isSubscriptionDetailsOpen = signal(false);
  subscriptionDetailsLoading = signal(false);

  downloadPdfLoading = signal(false);
  downloadXmlLoading = signal(false);
  cfdiPdfViewerOpening = signal(false);
  cfdiPdfViewerVisible = signal(false);
  cfdiPdfViewerSafeUrl = signal<SafeResourceUrl | null>(null);
  resendCfdiDialogVisible = signal(false);
  resendCfdiEmail = signal('');
  resendCfdiLoading = signal(false);
  resendCfdiInvoiceId = signal<number | null>(null);
  rowMenuItems = signal<MenuItem[]>([]);

  invoices = signal<InvoiceDTO[]>([]);
  loading = signal(false);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);
  currentPage = signal(0);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(0);
  pageSizeOptions = [20, 50, 100];

  skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8];

  private readonly allFilterValue = 'ALL';

  filtersForm = this.fb.group({
    folio: [''],
    serie: [''],
    invoiceId: [''],
    q: [''],
    status: this.fb.control<string>(this.allFilterValue),
    state: this.fb.control<string>(EntityState.ACTIVE),
    issueDateRange: this.fb.control<Date[] | null>(null)
  });

  hasActiveFilters = signal(false);
  private loadGeneration = 0;

  statusFilterOptions = [
    { value: this.allFilterValue, label: 'Todos' },
    { value: CfdiInvoiceStatusEnum.STAMPING, label: 'Timbrando' },
    { value: CfdiInvoiceStatusEnum.ACTIVE, label: 'Vigente' },
    { value: CfdiInvoiceStatusEnum.CANCELLATION_IN_PROGRESS, label: 'En cancelación' },
    { value: CfdiInvoiceStatusEnum.CANCELLED, label: 'Cancelado' }
  ];

  stateFilterOptions = [
    { value: this.allFilterValue, label: 'Todos' },
    { value: EntityState.ACTIVE, label: 'Activo' },
    { value: EntityState.INACTIVE, label: 'Inactivo' }
  ];

  constructor() {
    this.filtersForm.controls.folio.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    this.filtersForm.controls.serie.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    this.filtersForm.controls.invoiceId.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    this.filtersForm.controls.q.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    this.filtersForm.controls.status.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.applyFilters());

    this.filtersForm.controls.state.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.applyFilters());

    this.filtersForm.controls.issueDateRange.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((range) => {
        // Solo aplicar con rango completo o al limpiar; evita carrera al elegir la 1.ª fecha.
        if (range == null || range.length === 0 || this.hasCompleteDateRange(range)) {
          this.applyFilters();
        }
      });

    this.loadInvoices();
  }

  loadInvoices(): void {
    const generation = ++this.loadGeneration;
    this.loading.set(true);
    this.error.set(null);

    const spec = this.buildSpecFromFilters();
    const pageable = { page: this.currentPage(), size: this.pageSize(), sort: ['issueDate,desc'] };

    this.invoiceService.listInvoices(spec, pageable).subscribe({
      next: (response) => {
        if (generation !== this.loadGeneration) return;
        if (response.ok) {
          this.invoices.set(response.body ?? []);
          if (response.page) {
            this.totalItems.set(response.page.totalItems);
            this.totalPages.set(response.page.totalPages);
            this.pageSize.set(response.page.pageSize);
            this.currentPage.set(Math.max(0, response.page.currentPage - 1));
          } else {
            this.totalItems.set(response.body?.length ?? 0);
            this.totalPages.set(this.getTotalPages());
          }
        } else {
          this.error.set(response.message || 'Error al cargar facturas');
        }
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: (err) => {
        if (generation !== this.loadGeneration) return;
        this.error.set('Error al conectar con el servidor. Verifica que el API esté ejecutándose.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
        console.error('Error loading invoices:', err);
      }
    });
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const rows = event.rows || this.pageSize();
    const page = rows ? Math.floor((event.first ?? 0) / rows) : 0;
    if (page === this.currentPage() && rows === this.pageSize()) return;
    this.currentPage.set(page);
    this.pageSize.set(rows);
    this.loadInvoices();
  }

  openInvoiceDetails(invoice: InvoiceDTO): void {
    if (!invoice?.id) return;
    this.isDetailsOpen.set(true);
    this.selectedInvoice.set(null);
    this.detailsLoading.set(true);
    this.invoiceService.getInvoiceById(invoice.id).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedInvoice.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el detalle');
          this.closeDetails();
        }
        this.detailsLoading.set(false);
      },
      error: (err) => {
        this.detailsLoading.set(false);
        this.toastr.error('Error al cargar el detalle de la factura');
        this.closeDetails();
        console.error('Error loading invoice detail:', err);
      }
    });
  }

  closeDetails(): void {
    this.isDetailsOpen.set(false);
    this.selectedInvoice.set(null);
    this.detailsLoading.set(false);
  }

  onDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeDetails();
  }

  openSaleDetails(saleId: number | undefined, event?: Event): void {
    event?.stopPropagation();
    if (!saleId) return;
    this.isSaleDetailsOpen.set(true);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(true);
    this.saleService.getSaleById(saleId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          const detail = response.body;
          this.selectedSale.set(detail);
          this.saleService.getSaleCharges(detail.id!).subscribe({
            next: (chargesResp) => {
              this.saleCharges.set(chargesResp.body ?? []);
            },
            error: () => this.saleCharges.set([])
          });
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el detalle');
          this.closeSaleDetails();
        }
        this.saleDetailsLoading.set(false);
      },
      error: (err) => {
        this.saleDetailsLoading.set(false);
        this.toastr.error('Error al cargar el detalle de la venta');
        this.closeSaleDetails();
        console.error('Error loading sale detail:', err);
      }
    });
  }

  openSaleDetailsFromCustomer(saleId: number): void {
    this.openSaleDetails(saleId);
  }

  closeSaleDetails(): void {
    this.isSaleDetailsOpen.set(false);
    this.selectedSale.set(null);
    this.saleCharges.set([]);
    this.saleDetailsLoading.set(false);
  }

  onSaleDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSaleDetails();
  }

  editSaleFromModal(): void {
    const sale = this.selectedSale();
    if (!sale?.id) return;
    this.closeSaleDetails();
    this.router.navigate(['/sales', sale.id]);
  }

  openCustomerDetails(customerId: number | undefined, event?: Event): void {
    event?.stopPropagation();
    if (!customerId) return;
    this.isCustomerDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(true);
    this.customerService.getCustomerById(customerId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedCustomer.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el cliente');
          this.closeCustomerDetails();
        }
        this.customerDetailsLoading.set(false);
      },
      error: (err) => {
        this.customerDetailsLoading.set(false);
        this.toastr.error('Error al cargar el cliente');
        this.closeCustomerDetails();
        console.error('Error loading customer by id:', err);
      }
    });
  }

  closeCustomerDetails(): void {
    this.isCustomerDetailsOpen.set(false);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(false);
  }

  onCustomerDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeCustomerDetails();
  }

  editCustomerFromModal(): void {
    const customer = this.selectedCustomer();
    if (!customer?.id) return;
    this.closeCustomerDetails();
    this.router.navigate(['/customers', customer.id]);
  }

  openSubscriptionDetails(subscriptionId: number): void {
    if (!subscriptionId) return;
    this.isSubscriptionDetailsOpen.set(true);
    this.selectedSubscription.set(null);
    this.subscriptionDetailsLoading.set(true);
    this.subscriptionService.getSubscriptionById(subscriptionId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedSubscription.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar la suscripción');
          this.closeSubscriptionDetails();
        }
        this.subscriptionDetailsLoading.set(false);
      },
      error: (err) => {
        this.subscriptionDetailsLoading.set(false);
        this.toastr.error('Error al cargar la suscripción');
        this.closeSubscriptionDetails();
        console.error('Error loading subscription by id:', err);
      }
    });
  }

  closeSubscriptionDetails(): void {
    this.isSubscriptionDetailsOpen.set(false);
    this.selectedSubscription.set(null);
    this.subscriptionDetailsLoading.set(false);
  }

  onSubscriptionDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeSubscriptionDetails();
  }

  editSubscriptionFromModal(): void {
    const subscription = this.selectedSubscription();
    if (!subscription?.id) return;
    this.closeSubscriptionDetails();
    this.router.navigate(['/subscriptions', subscription.id]);
  }

  openRowMenu(event: Event, invoice: InvoiceDTO): void {
    event.stopPropagation();
    const resendBusy = this.resendCfdiLoading();
    this.rowMenuItems.set([
      {
        label: 'Ver factura',
        icon: 'pi pi-eye',
        disabled: !invoice.pdfFileId || invoice.id == null,
        command: () => this.openCfdiPdfViewer(invoice)
      },
      {
        label: 'Descargar PDF',
        icon: 'pi pi-file-pdf',
        disabled: !invoice.pdfFileId || invoice.id == null,
        command: () => this.downloadStampPDF(invoice)
      },
      {
        label: 'Descargar XML',
        icon: 'pi pi-file',
        disabled: !invoice.xmlFileId || invoice.id == null,
        command: () => this.downloadStampXML(invoice)
      },
      {
        label: 'Reenviar CFDI',
        icon: 'pi pi-send',
        disabled: resendBusy || invoice.id == null || (!invoice.pdfFileId && !invoice.xmlFileId),
        command: () => this.openResendCfdiDialog(invoice)
      }
    ]);
    this.rowActionsMenu?.toggle(event);
  }

  openResendCfdiDialog(invoice?: InvoiceDTO): void {
    const inv = invoice ?? this.selectedInvoice();
    if (inv?.id == null) return;
    this.resendCfdiInvoiceId.set(inv.id);
    this.resendCfdiEmail.set(inv.customer?.email ?? '');
    this.resendCfdiDialogVisible.set(true);
  }

  closeResendCfdiDialog(): void {
    if (this.resendCfdiLoading()) return;
    this.resendCfdiDialogVisible.set(false);
    this.resendCfdiEmail.set('');
    this.resendCfdiInvoiceId.set(null);
  }

  submitResendCfdi(): void {
    const invoiceId = this.resendCfdiInvoiceId() ?? this.selectedInvoice()?.id;
    const email = this.resendCfdiEmail().trim();
    if (invoiceId == null) return;
    if (!email) {
      this.toastr.error('Indica el correo electrónico al que se enviará el CFDI.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.toastr.error('El correo electrónico no es válido.');
      return;
    }
    this.resendCfdiLoading.set(true);
    this.invoiceService
      .resendCfdi(invoiceId, email)
      .pipe(finalize(() => this.resendCfdiLoading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok) {
            this.toastr.success(r.message ?? 'CFDI reenviado correctamente');
            this.resendCfdiDialogVisible.set(false);
            this.resendCfdiEmail.set('');
            this.resendCfdiInvoiceId.set(null);
          } else {
            this.toastr.error(apiFailureMessage(r, 'No se pudo reenviar el CFDI'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al reenviar el CFDI'));
        }
      });
  }

  openCfdiPdfViewer(invoice?: InvoiceDTO): void {
    const inv = invoice ?? this.selectedInvoice();
    if (inv?.id == null || this.cfdiPdfViewerOpening()) return;
    this.cfdiPdfViewerOpening.set(true);
    this.invoiceService
      .getStampPDF(inv.id)
      .pipe(finalize(() => this.cfdiPdfViewerOpening.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok && r.body?.content) {
            this.cfdiPdfViewerSafeUrl.set(this.stampPdfContentToSafeUrl(r.body.content));
            this.cfdiPdfViewerVisible.set(true);
          } else {
            this.toastr.error(apiFailureMessage(r, 'No se pudo obtener el PDF del CFDI'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al obtener el PDF'));
        }
      });
  }

  onCfdiPdfViewerVisibleChange(visible: boolean): void {
    this.cfdiPdfViewerVisible.set(visible);
    if (!visible) {
      this.cfdiPdfViewerSafeUrl.set(null);
    }
  }

  private normalizeStampPdfSrc(content: string): string {
    const t = content.trim();
    if (t.startsWith('http://') || t.startsWith('https://') || t.startsWith('blob:') || t.startsWith('data:')) {
      return t;
    }
    return `data:application/pdf;base64,${t}`;
  }

  private stampPdfContentToSafeUrl(content: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.normalizeStampPdfSrc(content));
  }

  downloadStampPDF(invoice?: InvoiceDTO): void {
    const inv = invoice ?? this.selectedInvoice();
    if (inv?.id == null) return;
    this.downloadPdfLoading.set(true);
    this.invoiceService
      .downloadStampPdfFile(inv.id)
      .pipe(finalize(() => this.downloadPdfLoading.set(false)))
      .subscribe({
        next: ({ blob, filename }) => {
          this.triggerBrowserDownload(blob, filename || `CFDI-${this.getInvoiceLabel(inv)}.pdf`);
          this.toastr.success('Descarga del PDF iniciada');
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al descargar el PDF'));
        }
      });
  }

  downloadStampXML(invoice?: InvoiceDTO): void {
    const inv = invoice ?? this.selectedInvoice();
    if (inv?.id == null) return;
    this.downloadXmlLoading.set(true);
    this.invoiceService
      .downloadStampXmlFile(inv.id)
      .pipe(finalize(() => this.downloadXmlLoading.set(false)))
      .subscribe({
        next: ({ blob, filename }) => {
          this.triggerBrowserDownload(blob, filename || `CFDI-${this.getInvoiceLabel(inv)}.xml`);
          this.toastr.success('Descarga del XML iniciada');
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al descargar el XML'));
        }
      });
  }

  applyFilters(): void {
    this.syncActiveFilters();
    this.currentPage.set(0);
    this.loadInvoices();
  }

  exportCsv(): void {
    if (!this.canExportInvoices || this.exporting()) return;
    this.exporting.set(true);
    this.invoiceService.exportInvoicesCsv(this.buildSpecFromFilters(), ['issueDate,desc']).subscribe({
      next: ({ blob, filename }) => {
        this.triggerBrowserDownload(blob, filename);
        this.exporting.set(false);
        this.toastr.success('CSV de facturas descargado');
      },
      error: (err: HttpErrorResponse) => {
        this.exporting.set(false);
        const fallback = 'No se pudo exportar el CSV de facturas';
        if (err.error instanceof Blob) {
          err.error.text().then((text) => {
            try {
              const json = JSON.parse(text) as { message?: string };
              this.toastr.error(json?.message?.trim() || fallback);
            } catch {
              this.toastr.error(fallback);
            }
          });
          return;
        }
        this.toastr.error(httpErrorApiMessage(err, fallback));
        console.error('Error exporting invoices CSV:', err);
      }
    });
  }

  clearFilters(): void {
    this.filtersForm.reset(
      {
        folio: '',
        serie: '',
        invoiceId: '',
        q: '',
        status: this.allFilterValue,
        state: EntityState.ACTIVE,
        issueDateRange: null
      },
      { emitEvent: false }
    );
    this.applyFilters();
  }

  getInvoiceLabel(invoice: InvoiceDTO): string {
    const serie = invoice.serie?.trim();
    const folio = invoice.folio?.trim();
    if (serie && folio) return `${serie}-${folio}`;
    return folio || serie || invoice.invoiceId || `#${invoice.id ?? ''}`;
  }

  getInvoiceStatusLabel(status?: string | null): string {
    switch (status) {
      case CfdiInvoiceStatusEnum.STAMPING:
        return 'Timbrando';
      case CfdiInvoiceStatusEnum.ACTIVE:
        return 'Vigente';
      case CfdiInvoiceStatusEnum.CANCELLATION_IN_PROGRESS:
        return 'En cancelación';
      case CfdiInvoiceStatusEnum.CANCELLED:
        return 'Cancelado';
      default:
        return status || '—';
    }
  }

  getInvoiceStatusSeverity(
    status?: string | null
  ): 'success' | 'warn' | 'danger' | 'secondary' | undefined {
    switch (status) {
      case CfdiInvoiceStatusEnum.STAMPING:
        return 'warn';
      case CfdiInvoiceStatusEnum.ACTIVE:
        return 'success';
      case CfdiInvoiceStatusEnum.CANCELLATION_IN_PROGRESS:
        return 'warn';
      case CfdiInvoiceStatusEnum.CANCELLED:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  getStateSeverity(state?: string): 'success' | 'danger' | null {
    if (state === EntityState.ACTIVE) return 'success';
    if (state === EntityState.INACTIVE) return 'danger';
    return null;
  }

  private syncActiveFilters(): void {
    const raw = this.filtersForm.getRawValue();
    this.hasActiveFilters.set(
      !!(
        raw.folio?.trim() ||
        raw.serie?.trim() ||
        raw.invoiceId?.trim() ||
        raw.q?.trim() ||
        (raw.status && raw.status !== this.allFilterValue) ||
        raw.state !== EntityState.ACTIVE ||
        this.hasCompleteDateRange(raw.issueDateRange)
      )
    );
  }

  private hasCompleteDateRange(range: Date[] | null | undefined): boolean {
    return !!range?.[0] && !!range[1];
  }

  private buildSpecFromFilters(): SearchInvoiceFilterSpec {
    const value = this.filtersForm.getRawValue();
    const spec: SearchInvoiceFilterSpec = {};

    if (value.folio?.trim()) spec['folio[lk]'] = value.folio.trim();
    if (value.serie?.trim()) spec['serie[lk]'] = value.serie.trim();
    if (value.invoiceId?.trim()) spec['invoiceId[lk]'] = value.invoiceId.trim();
    if (value.q?.trim()) spec['q[lk]'] = value.q.trim();
    if (value.status && value.status !== this.allFilterValue) {
      spec.status = value.status as CfdiInvoiceStatusEnumValue;
    }
    // ACTIVE por defecto lo aplica StateFilterSpec (defaultVal); solo enviamos state al desviarnos.
    const state = value.state ?? EntityState.ACTIVE;
    if (state === EntityState.INACTIVE) {
      spec.state = EntityState.INACTIVE;
    } else if (state !== EntityState.ACTIVE) {
      spec.state = [EntityState.ACTIVE, EntityState.INACTIVE];
    }
    this.applyIssueDateRange(spec, value.issueDateRange);

    return spec;
  }

  private applyIssueDateRange(spec: SearchInvoiceFilterSpec, range: Date[] | null | undefined): void {
    if (!this.hasCompleteDateRange(range) || !range?.[0] || !range[1]) return;
    const from = new Date(range[0]);
    from.setHours(0, 0, 0, 0);
    const toExclusive = new Date(range[1]);
    toExclusive.setHours(0, 0, 0, 0);
    toExclusive.setDate(toExclusive.getDate() + 1);
    spec['issueDate[gte]'] = from.toISOString();
    spec['issueDate[lt]'] = toExclusive.toISOString();
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private getTotalPages(): number {
    const total = this.totalPages();
    if (total > 0) return total;
    return Math.ceil(Math.max(0, this.totalItems()) / this.pageSize()) || 1;
  }
}
